import { useMemo } from "react";
import { Link } from "react-router-dom";

import {
  Copy,
  Globe,
  Link2,
  MapPin,
  Network,
  Pencil,
  Printer,
  Server,
  Share2,
  UserRound,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { FichaHostCard } from "@/features/servers/components/FichaHostCard";
import { FichaSoftwareCard } from "@/features/servers/components/FichaSoftwareCard";
import {
  useCenterInfra,
  useHosts,
  useSoftwareInstances,
} from "@/features/servers/hooks/useCenterInfra";
import { useInfraSettings } from "@/features/servers/hooks/useInfraSettings";
import { useSoftwareTypes } from "@/features/servers/hooks/useSoftwareTypes";
import { DEFAULT_OS_SUPPORT_POLICY } from "@/features/servers/schemas/servers";
import { buildFichaPlainText } from "@/features/servers/utils/ficha";
import { formatInfraAddress } from "@/features/servers/utils/address";
import type { User } from "@/features/users/schemas/user";
import { getUserFullName } from "@/features/users/utils/user-utils";
import { cn } from "@/shared/lib/utils";

interface CenterInfraFichaProps {
  institutionUid: string;
  user: User | null | undefined;
}

function KpiChip({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number | string;
  tone?: "default" | "warn" | "ok";
}) {
  return (
    <div
      className={cn(
        "rounded-lg border px-3 py-2 min-w-[6.5rem]",
        tone === "warn" && "border-amber-500/40 bg-amber-500/5",
        tone === "ok" && "border-emerald-500/40 bg-emerald-500/5"
      )}
    >
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
        {label}
      </p>
      <p className="text-lg font-semibold tabular-nums leading-tight mt-0.5">
        {value}
      </p>
    </div>
  );
}

/**
 * Vista de consulta organizada (read-only) de la ficha de infraestructura.
 * Verificación de hosts/software se hace aquí (marca rápida).
 */
export function CenterInfraFicha({
  institutionUid,
  user,
}: CenterInfraFichaProps) {
  const { data: infra, isLoading: loadingInfra } = useCenterInfra(institutionUid);
  const { data: hosts = [], isLoading: loadingHosts } = useHosts(institutionUid);
  const { data: instances = [], isLoading: loadingInstances } =
    useSoftwareInstances(institutionUid);
  const { data: softwareTypes = [] } = useSoftwareTypes();
  const { data: settings } = useInfraSettings();

  const isLoading = loadingInfra || loadingHosts || loadingInstances;
  const targets = settings?.softwareTargetVersions ?? {};
  const osPolicy = settings?.osSupportPolicy ?? DEFAULT_OS_SUPPORT_POLICY;

  const typeById = useMemo(() => {
    const map = new Map(softwareTypes.map((t) => [t.id, t]));
    for (const t of softwareTypes) map.set(t.key, t);
    return map;
  }, [softwareTypes]);

  const hostById = useMemo(
    () => new Map(hosts.map((h) => [h.id, h])),
    [hosts]
  );

  const addressLabel = formatInfraAddress(infra?.address);
  const centerName = user ? getUserFullName(user) : institutionUid;

  const hostsPending = hosts.filter((h) => !h.verifiedAt).length;
  const softwarePending = instances.filter((i) => !i.verifiedAt).length;

  const hasContent =
    !!addressLabel ||
    (infra?.contacts.length ?? 0) > 0 ||
    (infra?.publicIps.length ?? 0) > 0 ||
    (infra?.customFields.length ?? 0) > 0 ||
    hosts.length > 0 ||
    instances.length > 0;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Enlace de la ficha copiado");
    } catch {
      toast.error("No se pudo copiar el enlace");
    }
  };

  const handleCopyText = async () => {
    if (!infra) return;
    try {
      const text = buildFichaPlainText({
        user,
        infra,
        hosts,
        instances,
        softwareTypes,
      });
      await navigator.clipboard.writeText(text);
      toast.success("Ficha copiada como texto");
    } catch {
      toast.error("No se pudo copiar la ficha");
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-16" role="status" aria-live="polite">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="sr-only">Cargando ficha</span>
      </div>
    );
  }

  return (
    <div className="space-y-5 print:space-y-4" id="center-infra-ficha">
      {/* Header */}
      <header className="space-y-4 print:hidden">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 space-y-1">
            <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Ficha de infraestructura
            </p>
            <h1 className="text-2xl font-semibold tracking-tight truncate">
              {centerName}
            </h1>
            {user?.email && (
              <p className="text-sm text-muted-foreground truncate">{user.email}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              className="cursor-pointer"
              onClick={() => void handleCopyLink()}
            >
              <Link2 className="h-4 w-4 mr-1.5" aria-hidden />
              Copiar enlace
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="cursor-pointer"
              onClick={() => void handleCopyText()}
            >
              <Copy className="h-4 w-4 mr-1.5" aria-hidden />
              Copiar ficha
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="cursor-pointer"
              onClick={() => window.print()}
            >
              <Printer className="h-4 w-4 mr-1.5" aria-hidden />
              Imprimir
            </Button>
            <Button size="sm" className="cursor-pointer" asChild>
              <Link to={`/servers/${institutionUid}/edit`}>
                <Pencil className="h-4 w-4 mr-1.5" aria-hidden />
                Editar
              </Link>
            </Button>
          </div>
        </div>

        {hasContent && (
          <div className="flex flex-wrap gap-2">
            <KpiChip label="Hosts" value={hosts.length} />
            <KpiChip label="Software" value={instances.length} />
            <KpiChip
              label="Hosts sin verificar"
              value={hostsPending}
              tone={hostsPending > 0 ? "warn" : "ok"}
            />
            <KpiChip
              label="Software sin verificar"
              value={softwarePending}
              tone={softwarePending > 0 ? "warn" : "ok"}
            />
          </div>
        )}
      </header>

      <div className="hidden print:block">
        <h1 className="text-xl font-bold">{centerName}</h1>
        <p className="text-sm">Ficha de infraestructura ViewMed</p>
      </div>

      {!hasContent ? (
        <Card className="gap-3 py-4 shadow-none">
          <CardContent className="py-10 text-center space-y-3">
            <Share2 className="h-10 w-10 mx-auto text-muted-foreground" aria-hidden />
            <p className="text-sm text-muted-foreground">
              Este centro aún no tiene ficha de infraestructura.
            </p>
            <Button className="cursor-pointer" asChild>
              <Link to={`/servers/${institutionUid}/edit`}>
                <Pencil className="h-4 w-4 mr-2" aria-hidden />
                Completar ficha
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Centro */}
          <Card className="gap-3 py-4 shadow-none">
            <CardHeader className="px-4 pb-0 [.border-b]:pb-3 border-b">
              <CardTitle className="text-base flex items-center gap-2">
                <UserRound className="h-4 w-4 text-muted-foreground" aria-hidden />
                Centro
              </CardTitle>
              <CardDescription className="text-xs">
                Datos de ficha (no alteran el perfil del usuario)
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="sm:col-span-2 lg:col-span-3 space-y-1.5">
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" aria-hidden />
                  Dirección de infraestructura
                </p>
                <p className={cn("text-sm", !addressLabel && "text-muted-foreground")}>
                  {addressLabel || "Sin dirección de ficha"}
                </p>
              </div>

              <Separator className="sm:col-span-2 lg:col-span-3" />

              <div className="space-y-2">
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Contactos IT
                </p>
                {(infra?.contacts.length ?? 0) === 0 ? (
                  <p className="text-sm text-muted-foreground">—</p>
                ) : (
                  <ul className="space-y-2">
                    {infra!.contacts.map((c, i) => (
                      <li key={i} className="text-sm leading-snug">
                        <span className="font-medium">{c.name}</span>
                        {c.role && (
                          <span className="text-muted-foreground"> · {c.role}</span>
                        )}
                        <div className="text-xs text-muted-foreground">
                          {[c.phone, c.email].filter(Boolean).join(" · ")}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="space-y-2">
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                  <Globe className="h-3.5 w-3.5" aria-hidden />
                  IPs públicas
                </p>
                {(infra?.publicIps.length ?? 0) === 0 ? (
                  <p className="text-sm text-muted-foreground">—</p>
                ) : (
                  <ul className="space-y-1">
                    {infra!.publicIps.map((ip) => (
                      <li key={ip}>
                        <Badge variant="outline" className="font-mono text-xs">
                          {ip}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {(infra?.customFields.length ?? 0) > 0 && (
                <div className="space-y-2">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Campos custom
                  </p>
                  <dl className="space-y-1">
                    {infra!.customFields.map((f, i) => (
                      <div key={i} className="text-sm flex gap-2">
                        <dt className="font-medium shrink-0">{f.key}:</dt>
                        <dd className="text-muted-foreground break-all">{f.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Hosts */}
          <Card className="gap-3 py-4 shadow-none">
            <CardHeader className="px-4 pb-0 [.border-b]:pb-3 border-b">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Server className="h-4 w-4 text-muted-foreground" aria-hidden />
                  Hosts
                </CardTitle>
                <Badge variant="secondary">{hosts.length}</Badge>
              </div>
              <CardDescription className="text-xs">
                SO vs política de soporte · verificación desde esta ficha
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pt-3">
              {hosts.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">Sin hosts.</p>
              ) : (
                <div className="grid gap-2 md:grid-cols-2">
                  {hosts.map((host) => (
                    <FichaHostCard
                      key={host.id}
                      institutionUid={institutionUid}
                      host={host}
                      osPolicy={osPolicy}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Software */}
          <Card className="gap-3 py-4 shadow-none">
            <CardHeader className="px-4 pb-0 [.border-b]:pb-3 border-b">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Network className="h-4 w-4 text-muted-foreground" aria-hidden />
                  Software / Servicios
                </CardTitle>
                <Badge variant="secondary">{instances.length}</Badge>
              </div>
              <CardDescription className="text-xs">
                Versión vs target · verificación desde esta ficha
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pt-3 space-y-2">
              {instances.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">Sin software.</p>
              ) : (
                instances.map((inst) => {
                  const type = typeById.get(inst.softwareTypeId);
                  const typeLabel = type?.displayName ?? inst.softwareTypeId;
                  const targetVersion =
                    (type && (targets[type.key] || targets[type.id])) ||
                    targets[inst.softwareTypeId];
                  return (
                    <FichaSoftwareCard
                      key={inst.id}
                      institutionUid={institutionUid}
                      instance={inst}
                      typeLabel={typeLabel}
                      host={inst.hostId ? hostById.get(inst.hostId) : undefined}
                      targetVersion={targetVersion}
                    />
                  );
                })
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
